import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { isDefined } from '@fi-shared/utils';
import { isNullOrUndefined } from 'util';

@Component({
    selector: 'fi-autocomplete-items',
    templateUrl: './fi-autocomplete-items.component.html',
    styleUrls: ['./fi-autocomplete-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-autocomplete-items' },
    standalone: false,
})
export class FiAutocompleteItemsComponent implements OnInit, OnChanges {
    @Input() items: string[] = [];
    @Input() label: string;
    @Input() placeholder: string;
    @Input() displayValue: string;
    @Input() selectedItems: string[] = [];
    @Output() selectedItemsChange: EventEmitter<string[]> = new EventEmitter<string[]>();

    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    filteredItems$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    searchText: string;
    private filterDelayMS: number = 300;
    private filterTimer: NodeJS.Timeout;
    private readonly matOptionTagName: string = 'mat-option';

    ngOnInit() {
        this.filterItems();
    }

    ngOnChanges(changes: SimpleChanges) {
        const change: SimpleChange = changes.items;
        if (isDefined(change)) this.filterItems();
    }

    onSearchTextChanged() {
        if (isDefined(this.filterTimer)) {
            clearTimeout(this.filterTimer);
        }
        this.filterTimer = setTimeout(() => {
            this.filterItems();
        }, this.filterDelayMS);
    }

    private filterItems() {
        const filteredItems: string[] =
            isNullOrUndefined(this.searchText) || !this.searchText.trim().length
                ? this.items
                : this.items.filter((item: string) => {
                      return item.toLowerCase().includes(this.searchText?.toLowerCase());
                  });
        this.filteredItems$.next(filteredItems);
    }

    selectOnBlur(event: FocusEvent) {
        if (!this.searchText?.trim().length) {
            this.searchText = '';
            this.searchInput.nativeElement.value = '';
            return;
        }

        const target: HTMLElement = event.relatedTarget as HTMLElement;
        if (isNullOrUndefined(target) || target.tagName.toLowerCase() !== this.matOptionTagName) {
            this.onItemSelected(this.searchText.trim());
        }
    }

    onItemSelected(item: string) {
        if (this.isItemSelected(item)) return;
        const items: string[] = this.selectedItems;
        items.push(item.trim());
        this.searchInput.nativeElement.value = '';
        this.searchText = '';
        this.onSelectedItemsChange(items);
    }

    onItemRemoved(item: string) {
        const items: string[] = this.selectedItems;
        items.splice(items.indexOf(item), 1);
        this.onSelectedItemsChange(items);
    }

    isItemSelected(item: string): boolean {
        return !!this.selectedItems.find((selectedItem: string) => selectedItem.trim() === item.trim());
    }

    private onSelectedItemsChange(items: string[]) {
        this.selectedItems = items;
        this.selectedItemsChange.emit(this.selectedItems);
        this.filterItems();
    }

    protected readonly ENTER = ENTER;
    protected readonly COMMA = COMMA;
}
